import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, Search, Moon, Sun, User, LogOut, Bell, ChevronDown, Newspaper } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useWebsiteSettings } from '../contexts/WebsiteSettingsContext';
import { useCategories, useDistricts } from '../hooks/useCatalog';
import { localizeName } from '../i18n/translations';
import { Button } from './ui/button';
import Logo from './Logo';
import SocialIcons from './SocialIcons';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import AdBanner from './AdBanner';

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { lang, setLang, t } = useLanguage();
  const { tagline } = useWebsiteSettings();
  const navigate = useNavigate();
  const { data: categories = [] } = useCategories();
  const { data: districts = [] } = useDistricts();

  useEffect(() => {
    const handleScroll = () => {};
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  const today = new Date().toLocaleDateString(lang === 'kn' ? 'kn-IN' : 'en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  // Keep primary nav compact so utility links fit at common desktop widths (1280–1440).
  const mainCategories = categories.slice(0, 5);
  const moreCategories = categories.slice(5);
  const langSwitch = (
    <div className="flex items-center border border-white/30" data-testid="language-switcher">
      <button
        type="button"
        onClick={() => setLang('kn')}
        className={`px-2 py-0.5 text-[10px] font-label ${lang === 'kn' ? 'bg-primary text-white' : 'hover:text-red-500'}`}
        data-testid="lang-switch-kn"
      >
        ಕನ್ನಡ
      </button>
      <button
        type="button"
        onClick={() => setLang('en')}
        className={`px-2 py-0.5 text-[10px] font-label ${lang === 'en' ? 'bg-primary text-white' : 'hover:text-red-500'}`}
        data-testid="lang-switch-en"
      >
        EN
      </button>
    </div>
  );

  return (
    <header className="sticky top-0 z-50 w-full bg-white/95 dark:bg-black/95 backdrop-blur-xl border-b border-border" data-testid="site-header">
      {/* Top Bar */}
      <div className="hidden md:block bg-black text-white text-xs">
        <div className="max-w-[1400px] mx-auto px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="font-label">{today}</span>
            <span className="text-red-500">•</span>
            <span className="truncate max-w-[280px]">{tagline(lang)}</span>
          </div>
          <div className="flex items-center gap-4">
            {langSwitch}
            <Link to="/epaper" className="flex items-center gap-1 hover:text-red-500 transition-colors font-label" data-testid="epaper-top-link">
              <Newspaper size={14} /> {t('epaper')}
            </Link>
            <Link to="/contact" className="hover:text-red-500 transition-colors font-label" data-testid="contact-top-link">
              {t('contact')}
            </Link>
            <Link to="/social" className="hover:text-red-500 transition-colors font-label" data-testid="social-top-link">
              {t('connect')}
            </Link>
            <Link to="/developer" className="hover:text-red-500 transition-colors font-label" data-testid="developer-top-link">
              {t('developer')}
            </Link>
            <SocialIcons
              className="flex items-center gap-2"
              itemClassName="hover:text-red-500 transition-colors"
              size={14}
            />
            <span className="text-white/30">|</span>
            <button 
              onClick={toggleTheme}
              className="hover:text-red-500 transition-colors"
              data-testid="theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon size={14} /> : <Sun size={14} />}
            </button>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-1 hover:text-red-500 transition-colors" data-testid="user-menu-trigger">
                  <User size={14} />
                  <span>{user.name}</span>
                  <ChevronDown size={12} />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="rounded-none">
                  <DropdownMenuLabel>{user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {['SUPER_ADMIN', 'ADMIN'].includes(user.role) && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-dashboard-link">
                      Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  {['EDITOR', 'ADMIN', 'SUPER_ADMIN'].includes(user.role) && (
                    <DropdownMenuItem onClick={() => navigate('/editor')} data-testid="editor-panel-link">
                      Editor Panel
                    </DropdownMenuItem>
                  )}
                  {['REPORTER', 'EDITOR', 'ADMIN', 'SUPER_ADMIN'].includes(user.role) && (
                    <DropdownMenuItem onClick={() => navigate('/reporter')} data-testid="reporter-panel-link">
                      Reporter Panel
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                    <LogOut size={14} className="mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" className="hover:text-red-500 transition-colors font-label" data-testid="login-link">
                {t('login')}
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Main Header */}
      <div className="border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center" data-testid="site-logo">
            <Logo size="default" />
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            <div className="text-center">
              <p className="text-xs text-muted-foreground font-label">{t('weather')}</p>
              <p className="text-sm font-semibold">28°C ☀️</p>
            </div>
            <div className="text-center border-l border-border pl-6">
              <p className="text-xs text-muted-foreground font-label">Stock</p>
              <p className="text-sm font-semibold text-green-600">NIFTY ▲ 22,150</p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="lg:hidden flex items-center border-2 border-border" data-testid="language-switcher-mobile">
              <button
                type="button"
                onClick={() => setLang('kn')}
                className={`px-2 py-1 text-[10px] font-label ${lang === 'kn' ? 'bg-primary text-white' : ''}`}
                data-testid="lang-switch-kn"
              >
                ಕನ್ನಡ
              </button>
              <button
                type="button"
                onClick={() => setLang('en')}
                className={`px-2 py-1 text-[10px] font-label ${lang === 'en' ? 'bg-primary text-white' : ''}`}
                data-testid="lang-switch-en"
              >
                EN
              </button>
            </div>
            <button 
              onClick={() => setSearchOpen(!searchOpen)}
              className="p-2 hover:bg-muted transition-colors"
              data-testid="search-toggle-btn"
              aria-label={t('search')}
            >
              <Search size={20} />
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 hover:bg-muted transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Menu"
            >
              {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>

        {searchOpen && (
          <div className="border-t border-border bg-muted/30">
            <div className="max-w-[1400px] mx-auto px-4 py-3">
              <form onSubmit={handleSearch} className="flex gap-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('searchPlaceholder')}
                  className="flex-1 px-4 py-2 bg-white dark:bg-black border border-border focus:outline-none focus:border-primary"
                  data-testid="search-input"
                  autoFocus
                />
                <Button type="submit" className="rounded-none" data-testid="search-submit-btn">
                  {t('search')}
                </Button>
              </form>
            </div>
          </div>
        )}
      </div>

      {/* Navigation Menu — overflow-x containment prevents page-wide horizontal scroll on narrow desktops */}
      <nav className="hidden lg:block border-b border-border bg-white dark:bg-black overflow-x-auto">
        <div className="max-w-[1400px] mx-auto px-4 min-w-0">
          <ul className="flex items-center gap-1 min-w-0">
            <li className="shrink-0">
              <Link to="/" className="block px-4 py-3 text-sm font-label hover:bg-primary hover:text-white transition-colors" data-testid="nav-home">
                {t('home')}
              </Link>
            </li>
            {mainCategories.map((cat) => (
              <li key={cat.id} className="shrink-0">
                <Link
                  to={`/category/${cat.slug}`}
                  className="block px-4 py-3 text-sm font-label hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                  data-testid={`nav-category-${cat.slug}`}
                >
                  {localizeName(cat, lang)}
                </Link>
              </li>
            ))}
            {moreCategories.length > 0 && (
              <li className="shrink-0">
                <DropdownMenu>
                  <DropdownMenuTrigger className="px-4 py-3 text-sm font-label hover:bg-primary hover:text-white transition-colors flex items-center gap-1" data-testid="nav-more">
                    {t('more')} <ChevronDown size={14} />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="rounded-none">
                    {moreCategories.map((cat) => (
                      <DropdownMenuItem key={cat.id} onClick={() => navigate(`/category/${cat.slug}`)} data-testid={`dropdown-category-${cat.slug}`}>
                        {localizeName(cat, lang)}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </li>
            )}
            <li className="ml-auto flex items-center gap-1 shrink-0">
              <Link
                to="/contact"
                className="px-4 py-3 text-sm font-label hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                data-testid="nav-contact"
              >
                {t('contact')}
              </Link>
              <Link
                to="/social"
                className="px-4 py-3 text-sm font-label hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                data-testid="nav-social"
              >
                {t('connect')}
              </Link>
              <Link
                to="/developer"
                className="px-4 py-3 text-sm font-label hover:bg-primary hover:text-white transition-colors whitespace-nowrap"
                data-testid="nav-developer"
              >
                {t('developer')}
              </Link>
              <Link 
                to="/epaper" 
                className="px-4 py-3 text-sm font-label bg-black text-white hover:bg-primary transition-colors flex items-center gap-2 whitespace-nowrap"
                data-testid="nav-epaper"
              >
                <Newspaper size={14} /> {t('epaper')}
              </Link>
              <DropdownMenu key={`districts-dd-${lang}`}>
                <DropdownMenuTrigger className="px-4 py-3 text-sm font-label bg-primary text-white hover:bg-red-700 transition-colors flex items-center gap-1 whitespace-nowrap" data-testid="nav-districts">
                  {t('districts')} <ChevronDown size={14} />
                </DropdownMenuTrigger>
                <DropdownMenuContent className="rounded-none" key={`districts-content-${lang}`}>
                  {districts.map((d) => (
                    <DropdownMenuItem
                      key={`${lang}-${d.id}`}
                      onClick={() => navigate(`/district/${d.slug}`)}
                      data-testid={`dropdown-district-${d.slug}`}
                    >
                      {localizeName(d, lang)}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </li>
          </ul>
        </div>
      </nav>

      <div className="max-w-[1400px] mx-auto px-4 py-2">
        <AdBanner position="header" />
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden border-t border-border bg-white dark:bg-black max-h-[70vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-1">
            <Link to="/" className="block px-4 py-3 hover:bg-primary hover:text-white transition-colors font-label" onClick={() => setMobileMenuOpen(false)}>
              {t('home')}
            </Link>
            <Link 
              to="/epaper" 
              className="flex items-center gap-2 px-4 py-3 bg-black text-white hover:bg-primary transition-colors font-label"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-epaper-link"
            >
              <Newspaper size={16} /> {t('epaper')}
            </Link>
            <Link to="/contact" className="block px-4 py-3 hover:bg-primary hover:text-white transition-colors font-label" onClick={() => setMobileMenuOpen(false)}>
              {t('contact')}
            </Link>
            <Link to="/social" className="block px-4 py-3 hover:bg-primary hover:text-white transition-colors font-label" onClick={() => setMobileMenuOpen(false)}>
              {t('connect')}
            </Link>
            <Link to="/developer" className="block px-4 py-3 hover:bg-primary hover:text-white transition-colors font-label" onClick={() => setMobileMenuOpen(false)}>
              {t('developer')}
            </Link>
            <div className="pt-2 pb-1 px-4 text-xs font-label text-muted-foreground">{t('categories')}</div>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/category/${cat.slug}`}
                className="block px-4 py-2 hover:bg-primary hover:text-white transition-colors text-sm"
                onClick={() => setMobileMenuOpen(false)}
              >
                {localizeName(cat, lang)}
              </Link>
            ))}
            <div className="pt-2 pb-1 px-4 text-xs font-label text-muted-foreground">{t('districts')}</div>
            {districts.map((d) => (
              <Link
                key={`${lang}-mobile-district-${d.id}`}
                to={`/district/${d.slug}`}
                className="block px-4 py-2 hover:bg-primary hover:text-white transition-colors text-sm"
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-district-${d.slug}`}
              >
                {localizeName(d, lang)}
              </Link>
            ))}
            <div className="pt-3 border-t border-border">
              {user ? (
                <>
                  <div className="px-4 py-2 text-sm">Signed in as <strong>{user.name}</strong></div>
                  {['SUPER_ADMIN', 'ADMIN'].includes(user.role) && (
                    <Link to="/admin" className="block px-4 py-2 hover:bg-primary hover:text-white transition-colors text-sm" onClick={() => setMobileMenuOpen(false)}>
                      Admin Dashboard
                    </Link>
                  )}
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="block w-full text-left px-4 py-2 hover:bg-primary hover:text-white transition-colors text-sm">
                    {t('logout')}
                  </button>
                </>
              ) : (
                <Link to="/login" className="block px-4 py-2 hover:bg-primary hover:text-white transition-colors text-sm" onClick={() => setMobileMenuOpen(false)}>
                  {t('login')}
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
